const CHART_COLORS = [
  "#60a5fa", // blue-400
  "#f87171", // red-400
  "#4ade80", // green-400
  "#fbbf24", // yellow-400
  "#c084fc", // purple-400
  "#fb923c", // orange-400
  "#22d3ee", // cyan-400
  "#f472b6", // pink-400
] as const;

export const CHANNEL_TINTS = [
  { bg: "#0c1830", border: "#1e3a5f", text: "#93c5fd", glow: "rgba(59,130,246,0.6)", dot: "#60a5fa" },
  { bg: "#1a0c0c", border: "#5c1d1d", text: "#fca5a5", glow: "rgba(248,113,113,0.6)", dot: "#f87171" },
  { bg: "#0c1a0c", border: "#1d5c1d", text: "#86efac", glow: "rgba(74,222,128,0.6)", dot: "#4ade80" },
  { bg: "#1a1505", border: "#5c4a09", text: "#fcd34d", glow: "rgba(251,191,36,0.6)", dot: "#fbbf24" },
  { bg: "#130c1e", border: "#3b1d5e", text: "#d8b4fe", glow: "rgba(192,132,252,0.6)", dot: "#c084fc" },
  { bg: "#1a1005", border: "#5c3209", text: "#fdba74", glow: "rgba(251,146,60,0.6)", dot: "#fb923c" },
  { bg: "#0c1a1a", border: "#1d5c5c", text: "#67e8f9", glow: "rgba(34,211,238,0.6)", dot: "#22d3ee" },
  { bg: "#1a0c18", border: "#5c1d4a", text: "#f9a8d4", glow: "rgba(244,114,182,0.6)", dot: "#f472b6" },
] as const;

export type ChannelColorIndices = Record<string, number>;

function getChannelColorIndex(
  channel: string,
  channelColorIndices: ChannelColorIndices | undefined,
  fallbackIndex: number,
): number {
  const colorIndex = channelColorIndices?.[channel];
  return typeof colorIndex === "number" &&
    Number.isInteger(colorIndex) &&
    colorIndex >= 0
    ? colorIndex
    : fallbackIndex;
}

export function getChannelColor(
  channel: string,
  channelColorIndices: ChannelColorIndices | undefined,
  fallbackIndex: number,
): string {
  return CHART_COLORS[
    getChannelColorIndex(channel, channelColorIndices, fallbackIndex) %
      CHART_COLORS.length
  ];
}

export function getChannelTint(
  channel: string,
  channelColorIndices: ChannelColorIndices | undefined,
  fallbackIndex: number,
): (typeof CHANNEL_TINTS)[number] {
  return CHANNEL_TINTS[
    getChannelColorIndex(channel, channelColorIndices, fallbackIndex) %
      CHANNEL_TINTS.length
  ];
}

export function reconcileChannelColorIndices(
  previousColorIndices: ChannelColorIndices | undefined,
  previousSelectedChannels: string[],
  nextSelectedChannels: string[],
): ChannelColorIndices {
  const seededColorIndices: ChannelColorIndices = { ...previousColorIndices };

  previousSelectedChannels.forEach((channel, index) => {
    const seededColorIndex = seededColorIndices[channel];
    if (
      typeof seededColorIndex !== "number" ||
      !Number.isInteger(seededColorIndex) ||
      seededColorIndex < 0
    ) {
      seededColorIndices[channel] = index % CHART_COLORS.length;
    }
  });

  const nextColorIndices: ChannelColorIndices = {};

  nextSelectedChannels.forEach((channel) => {
    const existingColorIndex = seededColorIndices[channel];
    if (
      typeof existingColorIndex === "number" &&
      Number.isInteger(existingColorIndex) &&
      existingColorIndex >= 0
    ) {
      nextColorIndices[channel] = existingColorIndex;
      return;
    }

    const usedColorIndices = new Set(Object.values(nextColorIndices));
    const availableColorIndex = CHART_COLORS.findIndex(
      (_, colorIndex) => !usedColorIndices.has(colorIndex),
    );

    nextColorIndices[channel] =
      availableColorIndex === -1
        ? Object.keys(nextColorIndices).length % CHART_COLORS.length
        : availableColorIndex;
  });

  return nextColorIndices;
}
